import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import * as m from 'motion/react-m';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';

import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system/button';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import Share2Icon from '@flipster/react/icon/generated/Share2Icon';
import ShareIcon from '@flipster/react/icon/generated/ShareIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { Styles, css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { TradingLeaderboardEvent } from '@flipster/utils/segment/events/trading-leaderboard';

import { RankShareModal } from '../modal/rank-share';
import { RankShareInitialIntervalContext } from '../modal/rank-share/bunja';
import { MyRankItemId } from '../rank-list/item';
import { RankChangeToday } from '../shared/rank-change-today';
import { LeaderboardTierIcon } from '../shared/tier-icon';
import { myInfoBunja } from './bunja';

interface MyInfoSectionProps {
  css: Styles;
}
export const MyInfoSection = ({ css: cssProps }: MyInfoSectionProps) => {
  const router = useRouter();
  const { isMobile } = useBreakpoint();
  const {
    isLoggedInAtom,
    rightSectionAtom,
    currentCursorRankingAtom,
    formattedRankAtom,
    isOutOfRankingAtom,
    optOutAtom,
    nicknameAtom,
  } = useBunja(myInfoBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const nickname = useAtomValue(nicknameAtom);
  const rightSection = useAtomValue(rightSectionAtom);
  const formattedRank = useAtomValue(formattedRankAtom);
  const isOutOfRanking = useAtomValue(isOutOfRankingAtom);
  const hideMyRankAndPnl = useAtomValue(optOutAtom);
  const myRanking = useAtomValue(currentCursorRankingAtom);
  const status = myRanking?.rankingStatus;

  if (!isLoggedIn) return;

  const MyRankAndPnl = () => {
    if (!myRanking || hideMyRankAndPnl || status === 'OPT_IN_PENDING') return null;
    const isUnranked = status === 'UNRANKED';
    return (
      <button
        type='button'
        className={hstack({
          tabletDown: { w: '100%', h: 31, gap: 8, justifyContent: 'space-between' },
          tablet: { h: 23, gap: 32 },
        })}
        onClick={() => {
          document.getElementById(MyRankItemId)?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }}
      >
        <div className={hstack({ gap: { base: 4, tablet: 10 } })}>
          <div className={hstack({ gap: { base: 4, tablet: 8 } })}>
            <LeaderboardTierIcon tier={myRanking.tier} size={20} />
            <span
              className={css({
                textStyle: 'Text/M/Regular',
                color: 'content.default.level-2',
                display: { base: 'none', tablet: 'initial' },
              })}
            >
              <FormattedMessage id='Autogen_common.r251126.s_Ranking' />
            </span>
          </div>
          <span
            className={css({
              textStyle: 'Title/XS',
              color: isUnranked ? 'content.default.level-3' : 'content.default.level-5',
            })}
          >
            {formattedRank}
          </span>
          {!isOutOfRanking && <ChevronRightIcon size={12} color={token('colors.content.default.level-2')} />}
          <RankChangeToday delta={myRanking.dailyRankChange ?? 0} />
        </div>
        <div className={hstack({ gap: 10 })}>
          <span
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-2',
              display: { base: 'none', tablet: 'initial' },
            })}
          >
            <FormattedMessage id='Autogen_common.r251126.s_PnL_USDT' />
          </span>
          {isUnranked ? (
            <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-3' })}>-</p>
          ) : (
            <Formatted.Number
              value={myRanking.pnl}
              withComma
              withPlus
              withColor
              toFixed
              precision={2}
              css={{ textStyle: 'Title/XS', color: 'content.default.level-3' }}
            />
          )}
        </div>
      </button>
    );
  };

  return (
    <m.div
      className={css(
        {
          tabletDown: {
            width: '100%',
            pos: 'fixed',
            left: 0,
            bottom: 0,
            bg: 'linear-gradient(179deg, #1C1C1C 46.21%, #111 87.14%)',
            borderTopRadius: 16,
            p: '24px 16px 16px',
            zIndex: 10,
          },
          tablet: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bg: 'background.default.level-1',
            p: 24,
            rounded: 8,
          },
        },
        cssProps,
      )}
      key='bottomSheet'
      initial={isMobile && { transform: 'translateY(100%)' }}
      animate={isMobile && { transform: 'translateY(0px)' }}
    >
      <div className={stack({ gap: 12, tabletDown: { mb: 12 } })}>
        <div
          className={hstack({
            justifyContent: { base: 'space-between', tablet: 'flex-start' },
          })}
        >
          <button
            type='button'
            onClick={() => {
              segment.track(TradingLeaderboardEvent.RankingPageMyRankClick, {
                realized_pnl: myRanking?.pnl ? Number(myRanking?.pnl) : null,
                challenge_rank: formattedRank,
              });
              router.push('/user/profile');
            }}
          >
            <span
              className={css({
                tabletDown: { textStyle: 'Text/M/Strong', color: 'content.default.level-3' },
                tablet: { textStyle: 'Title/XS', color: 'content.default.level-5' },
              })}
            >
              {nickname}
            </span>
          </button>
          {isMobile && rightSection.shareButton && <ShareRankMobileButton />}
        </div>
        <MyRankAndPnl />
      </div>
      <div
        className={hstack({
          tabletDown: {
            gap: 8,
            justifyContent: 'space-between',
            bg: 'background.default.level-3',
            rounded: 8,
            p: '8px 12px',
          },
          tablet: {
            gap: 16,
          },
        })}
      >
        <span
          className={css({
            textStyle: 'Text/M/Regular',
            color: 'content.default.level-2',
            flex: 1,
            textAlign: { base: 'unset', tablet: 'right' },
          })}
        >
          {rightSection.description}
        </span>
        {rightSection.primaryButton &&
          (isMobile ? (
            <TextButton label={rightSection.primaryButton.label} onClick={rightSection.primaryButton.onClick} />
          ) : (
            <div className={hstack({ gap: 8 })}>
              <Button label={rightSection.primaryButton.label} onClick={rightSection.primaryButton.onClick} />
              {rightSection.shareButton && <ShareRankTabletButton />}
            </div>
          ))}
      </div>
    </m.div>
  );
};

const ShareRankTabletButton = () => {
  const t = useTranslations();
  const overlay = useOverlay();
  const { interval, tier, currentCursorRankingAtom } = useBunja(myInfoBunja);
  const myRanking = useAtomValue(currentCursorRankingAtom);
  return (
    <Button
      hierarchy='secondary'
      color='neutral'
      size='medium'
      label={t('Autogen_common.r251126.s_Share_rank')}
      icon={<Share2Icon />}
      onClick={() => {
        segment.track(TradingLeaderboardEvent.LeaderboardShareRankClick, { ranking: myRanking?.rank, interval, tier });
        overlay.open(({ isOpen, onClose }) => (
          <RankShareInitialIntervalContext value={interval}>
            <RankShareModal isOpen={isOpen} onClose={onClose} />
          </RankShareInitialIntervalContext>
        ));
      }}
    />
  );
};

const ShareRankMobileButton = () => {
  const overlay = useOverlay();
  const { interval, tier, currentCursorRankingAtom } = useBunja(myInfoBunja);
  const myRanking = useAtomValue(currentCursorRankingAtom);
  return (
    <IconButton
      hierarchy='quaternary'
      size='small'
      icon={<ShareIcon />}
      onClick={() => {
        segment.track(TradingLeaderboardEvent.LeaderboardShareRankClick, { ranking: myRanking?.rank, interval, tier });
        overlay.open(({ isOpen, onClose }) => (
          <RankShareInitialIntervalContext value={interval}>
            <RankShareModal isOpen={isOpen} onClose={onClose} />
          </RankShareInitialIntervalContext>
        ));
      }}
    />
  );
};
